import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import {
  domainCutover,
  regionConfig,
  alternateBaseUrl,
  alternateRegion,
} from '@/config/region';

const DISMISS_KEY = 'region-suggestion-dismissed';

const readRegionCookie = (): 'US' | 'UK' | null => {
  const match = document.cookie.match(/(?:^|;\s*)storily_region=(US|UK)/);
  return match ? (match[1] as 'US' | 'UK') : null;
};

/**
 * SEO-safe cross-region prompt. The geo-region edge function tags every
 * visitor with a storily_region cookie; when that disagrees with the region
 * this build serves, we *suggest* the other site instead of redirecting —
 * forced geo-redirects would bounce Googlebot (which crawls from US IPs)
 * off the UK apex and prevent it from ever being indexed.
 *
 * Inert until VITE_DOMAIN_CUTOVER is enabled. Never appears in prerendered
 * HTML: prerendering runs against a local preview server with no edge
 * function, so the cookie is absent there.
 */
const RegionSuggestionBanner = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!domainCutover) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    const detected = readRegionCookie();
    if (detected && detected !== regionConfig.region) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setVisible(false);
  };

  const flag = alternateRegion === 'UK' ? '🇬🇧' : '🇺🇸';
  const regionLabel = alternateRegion === 'UK' ? 'UK' : 'US';

  return (
    <div
      role="region"
      aria-label="Regional site suggestion"
      className="fixed bottom-4 left-1/2 z-[100] flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-center gap-3 rounded-xl bg-zinc-900 px-4 py-3 text-sm text-white shadow-xl"
    >
      <span aria-hidden="true" className="text-base">{flag}</span>
      <p className="flex-1 leading-snug">
        It looks like you're in the {regionLabel}.{' '}
        <a
          href={`${alternateBaseUrl}${pathname}`}
          className="font-semibold underline underline-offset-2 hover:opacity-80"
        >
          Switch to our {regionLabel} site
        </a>
      </p>
      <button
        onClick={dismiss}
        aria-label="Dismiss and stay on this site"
        className="rounded-md p-1 transition-opacity hover:opacity-70"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default RegionSuggestionBanner;
